import React, { Suspense, lazy, useState, useEffect, useCallback, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import useStore from '@/store';
import TopControls from '@/components/common/TopControls';
import ErrorFallback from '@/components/common/ErrorBoundary';
import { ScreenErrorBoundary } from '@/components/common/ErrorBoundaryWrapper';
import BackgroundCamera from '@/components/ui/BackgroundCamera';
import AuthBackground from '@/components/ui/AuthBackground';
import FloorPlanBottomSheet from '@/components/ui/FloorPlanBottomSheet';
import logger from '@/utils/logger';

// Lazy load components
const LoginScreen = lazy(() => import('@/components/screens/LoginScreen'));
const SetProjectScreen = lazy(() => import('@/components/screens/SetProjectScreen'));
const CameraScreen = lazy(() => import('@/components/screens/CameraScreen'));
const PhotoUploadScreen = lazy(() => import('@/components/screens/PhotoUploadScreen'));
const PositioningScreen = lazy(() => import('@/components/screens/PositioningScreen'));
const ResultScreen = lazy(() => import('@/components/screens/ResultScreen'));
const MapFloorPlanChoiceScreen = lazy(() => import('@/components/screens/MapFloorPlanChoiceScreen'));
const MapChoiceScreen = lazy(() => import('@/components/screens/MapChoiceScreen'));
const AnnotationScreen = lazy(() => import('@/components/screens/AnnotationScreen'));
const VoiceNoteScreen = lazy(() => import('@/components/screens/VoiceNoteScreen'));
const PinPlacementModal = lazy(() => import('@/components/ui/PinPlacementModal'));
const OrganizationScreen = lazy(() => import('@/components/screens/OrganizationScreen'));
const PaywallScreen = lazy(() => import('@/components/screens/PaywallScreen'));
const OnboardingScreen = lazy(() => import('@/components/screens/OnboardingScreen'));
const BillingScreen = lazy(() => import('@/components/screens/BillingScreen'));
import CheckoutReturnHandler from '@/components/CheckoutReturnHandler';

/** Screen identifiers that mount the live camera feed in the background. */
type CameraBearingScreen = 'camera' | 'positioning' | 'annotation' | 'projects';

// Helper to determine which screens need camera access
const needsCamera = (screen: string): boolean => {
    const cameraScreens: readonly CameraBearingScreen[] = ['camera', 'positioning', 'annotation', 'projects'];
    return (cameraScreens as readonly string[]).includes(screen);
};

/**
 * Background preload of the on-device Whisper model. Lazy-imports
 * WhisperEngine so the heavy transformers.js code stays out of the
 * initial bundle until login. After the first call, the model lives
 * in the browser's Cache Storage and subsequent invocations
 * (across reloads, tab closes, installs to home screen) are
 * effectively no-ops.
 *
 * Errors here are logged at warn (not error) and otherwise swallowed —
 * the voice-note screen retries on entry and surfaces any genuine
 * failure to the user there.
 */
let __preloadStarted = false;
async function preloadWhisperModel(): Promise<void> {
    if (__preloadStarted) return;
    __preloadStarted = true;
    try {
        const { getWhisperEngine } = await import('@/services/transcription/WhisperEngine');
        await getWhisperEngine().load();
        logger.info('Whisper model preload complete');
    } catch (err: unknown) {
        // Reset the flag on failure so the voice-note screen entry can
        // retry from scratch. Without this, a transient network error at
        // boot would permanently disable retries this session.
        __preloadStarted = false;
        logger.warn('Whisper model preload failed (will retry on demand)', {
            error: err instanceof Error ? err.message : String(err),
        });
    }
}

// Inline boot-state UI components. Kept here (not split into their own
// files) because they are App-shell concerns: they never render anywhere
// else, and they need to share the AuthBackground frame the shell already
// sets up.
const BootLoading: React.FC = () => (
    <div className="relative flex flex-1 flex-col items-center justify-center text-center text-yellow-400 p-6">
        <div className="inline-flex items-center gap-3 select-none mb-8">
            <img
                src="/icon-512.png"
                alt="PinCam"
                style={{ width: '56px', height: '56px' }}
                className="flex-shrink-0 drop-shadow-[0_2px_12px_rgba(250,204,21,0.25)]"
            />
            <h1
                className="text-4xl font-bold tracking-tight text-yellow-300 leading-none"
                style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
            >
                PinCam
            </h1>
        </div>
        <div
            aria-hidden="true"
            className="rounded-full border-4 border-yellow-400/30 border-t-yellow-300 animate-spin"
            style={{ width: '36px', height: '36px' }}
        />
        <p className="text-sm font-mono text-yellow-400/70 mt-4 tracking-wide">Connessione al server…</p>
    </div>
);

interface BootErrorProps {
    onRetry: () => void;
    onLogout: () => void;
}

const BootError: React.FC<BootErrorProps> = ({ onRetry, onLogout }) => (
    <div className="relative flex flex-1 flex-col items-center justify-center text-center text-yellow-400 p-6">
        <div className="inline-flex items-center gap-3 select-none mb-6">
            <img
                src="/icon-512.png"
                alt="PinCam"
                style={{ width: '48px', height: '48px' }}
                className="flex-shrink-0 opacity-60"
            />
            <h1
                className="text-3xl font-bold tracking-tight text-yellow-300/80 leading-none"
                style={{ fontFamily: "'Source Sans Pro', sans-serif" }}
            >
                PinCam
            </h1>
        </div>
        <h2 className="text-xl font-mono font-bold text-yellow-300 mb-2">Impossibile contattare il server</h2>
        <p className="text-sm font-mono text-yellow-400/70 max-w-md mb-8">
            Controlla la connessione e riprova. Se il problema persiste, esci e accedi di nuovo.
        </p>
        <div className="flex flex-col gap-3 w-full" style={{ maxWidth: '320px' }}>
            <button
                type="button"
                onClick={onRetry}
                className="bg-gradient-to-b from-yellow-300 to-yellow-500 text-black border border-black hover:from-yellow-200 hover:to-yellow-400 font-bold shadow-[inset_0_1px_0_rgba(255,255,255,0.4),0_4px_14px_rgba(250,204,21,0.35)] rounded-xl py-3 font-mono tracking-wide"
                data-testid="boot-error-retry"
            >
                Riprova
            </button>
            <button
                type="button"
                onClick={onLogout}
                className="bg-black/40 text-yellow-300 border border-yellow-400/60 hover:bg-black/60 rounded-xl py-3 font-mono tracking-wide"
                data-testid="boot-error-logout"
            >
                Esci
            </button>
        </div>
    </div>
);

/**
 * Boot-phase status:
 * - 'loading' — cold-start fetches in flight; only boot UI renders.
 * - 'ready'   — subscription known; app chrome + screens render.
 * - 'failed'  — retries exhausted; BootError offers retry/logout.
 */
type BootStatus = 'loading' | 'ready' | 'failed';

/** Outcome of a single boot fetch attempt. `sub` carries the freshly
 *  fetched subscription on success, `null` otherwise. Shape is left loose
 *  (the slice owns the canonical Subscription type) but narrowed at the
 *  one place we read `onboardingCompleted`. */
type BootAttemptResult =
    | { result: 'success'; sub: { onboardingCompleted?: boolean } | null }
    | { result: 'fail'; sub: null };

function App(): React.JSX.Element {
    const screen = useStore((state) => state.screen);
    const isAuthenticated = useStore((state) => state.isAuthenticated);
    const logout = useStore((state) => state.logout);
    const handleBack = useStore((state) => state.handleBack);
    const subscription = useStore((state) => state.subscription);
    const [cameraReady, setCameraReady] = useState(false);
    const [debugInfo, setDebugInfo] = useState('');
    // Boot phase: the post-auth fetches that turn the empty shell into a
    // usable app. Until 'ready', the only screens that render are the boot
    // ones (BootLoading / BootError) — never CameraScreen with no data,
    // which is the half-broken state the user reported.
    const [bootStatus, setBootStatus] = useState<BootStatus>('loading');
    const bootTimerRef = useRef<number | null>(null);

    // Stable callbacks for BackgroundCamera props to prevent infinite loops
    const handleSetCameraReady = useCallback((ready: boolean) => {
        setCameraReady(ready);
    }, []);

    const handleSetDebugInfo = useCallback((info: string) => {
        setDebugInfo(info);
    }, []);

    const handleErrorReset = useCallback(() => {
        window.location.reload();
    }, []);

    // Single attempt of the boot fetches with a hard timeout. Returns
    // 'success' iff fetchSubscription resolved (projects/org are
    // best-effort and don't gate the UI). 'fail' on rejection or timeout.
    const runBootAttempt = useCallback(async (): Promise<BootAttemptResult> => {
        const store = useStore.getState();
        const ATTEMPT_TIMEOUT_MS = 12000;

        let timeoutHandle: number | undefined;
        const timeoutPromise = new Promise<'timeout'>(resolve => {
            timeoutHandle = window.setTimeout(() => resolve('timeout'), ATTEMPT_TIMEOUT_MS);
        });

        const fetches = Promise.allSettled([
            store.fetchProjects(),
            store.fetchOrgData(),
            store.fetchSubscription(),
        ]);

        const raced = await Promise.race([fetches, timeoutPromise]);
        if (timeoutHandle !== undefined) window.clearTimeout(timeoutHandle);

        if (raced === 'timeout') {
            logger.warn('Boot attempt timed out', { timeoutMs: ATTEMPT_TIMEOUT_MS });
            return { result: 'fail', sub: null };
        }

        const settled = raced as PromiseSettledResult<unknown>[];
        const subResult = settled[2];

        // fetchSubscription swallows errors and returns null on failure
        // (see src/store/subscriptionSlice.ts). So a "fulfilled" promise
        // with a null value is functionally a failed fetch — treat it the
        // same as a rejection, otherwise we'd flip to 'ready' against an
        // empty store and re-introduce the half-broken state we're fixing.
        if (subResult.status === 'fulfilled' && subResult.value) {
            return {
                result: 'success',
                sub: subResult.value as { onboardingCompleted?: boolean },
            };
        }
        logger.warn('Boot subscription fetch failed', {
            rejected: subResult.status === 'rejected',
            error: subResult.status === 'rejected'
                ? (subResult.reason as { message?: string })?.message
                : 'returned null',
        });
        return { result: 'fail', sub: null };
    }, []);

    // Drives the post-auth boot sequence with retry+backoff. Skips the
    // 'loading' UI if subscription is already populated (fresh login path
    // already seeded it via routePostLogin) and only does a silent refresh.
    // On every fail, retries on 5s/15s/30s; after the last retry, surfaces
    // BootError to the user. Cancellable: returns a stop() so the useEffect
    // cleanup can abort the loop on unmount/logout.
    const performBootCycle = useCallback((): (() => void) => {
        let cancelled = false;
        const RETRY_DELAYS_MS = [5000, 15000, 30000];

        const stop = (): void => {
            cancelled = true;
            if (bootTimerRef.current !== null) {
                window.clearTimeout(bootTimerRef.current);
                bootTimerRef.current = null;
            }
        };

        const wait = (ms: number): Promise<void> =>
            new Promise(resolve => {
                bootTimerRef.current = window.setTimeout(() => {
                    bootTimerRef.current = null;
                    resolve();
                }, ms);
            });

        void (async () => {
            const alreadyHasSub = !!useStore.getState().subscription;

            if (alreadyHasSub) {
                // Fresh-login path: subscription already in the store from
                // LoginScreen / OAuth handler. Show the app immediately,
                // refresh in background, swallow transient failures.
                setBootStatus('ready');
                const { result } = await runBootAttempt();
                if (cancelled) return;
                if (result === 'fail') {
                    logger.info('Background boot refresh failed; keeping cached subscription');
                }
                return;
            }

            // Cold-start path (refresh with cached token): block the UI on
            // a loading screen until subscription comes back or we give up.
            setBootStatus('loading');

            let attempt = 0;
            for (;;) {
                if (cancelled) return;
                const { result, sub } = await runBootAttempt();
                if (cancelled) return;

                if (result === 'success') {
                    setBootStatus('ready');
                    // Mandatory onboarding gate (mirrors LoginScreen.routePostLogin).
                    if (sub && !sub.onboardingCompleted && useStore.getState().screen !== 'onboarding') {
                        useStore.getState().setCurrentScreen('onboarding');
                    }
                    return;
                }

                if (attempt >= RETRY_DELAYS_MS.length) {
                    setBootStatus('failed');
                    return;
                }

                const delay = RETRY_DELAYS_MS[attempt];
                logger.info('Boot retry scheduled', { attempt: attempt + 1, delayMs: delay });
                await wait(delay);
                attempt += 1;
            }
        })();

        return stop;
    }, [runBootAttempt]);

    // Manual retry from BootError. Cancels any pending timer and restarts
    // the cycle from scratch. The stop returned by performBootCycle is
    // captured by the wrapping useEffect via a ref-driven re-trigger:
    // simplest implementation is to bump a counter that the useEffect
    // depends on. Here we just invoke performBootCycle inline and let the
    // next useEffect run (on next isAuthenticated/cycle change) handle its
    // own cleanup. We need to keep the latest stop fn so manual retry can
    // cancel it before starting a new one.
    const bootCycleStopRef = useRef<(() => void) | null>(null);

    const handleBootRetry = useCallback((): void => {
        bootCycleStopRef.current?.();
        bootCycleStopRef.current = performBootCycle();
    }, [performBootCycle]);

    const handleBootLogout = useCallback((): void => {
        bootCycleStopRef.current?.();
        bootCycleStopRef.current = null;
        logout();
    }, [logout]);

    useEffect(() => {
        if (!isAuthenticated) {
            // Reset boot state so the next login starts fresh.
            setBootStatus('loading');
            bootCycleStopRef.current?.();
            bootCycleStopRef.current = null;
            return;
        }

        // CLAUDE.md rule 4: read the store via getState() inside the effect
        // so we never close over a stale snapshot of its actions/state.
        const store = useStore.getState();

        bootCycleStopRef.current = performBootCycle();

        // Only redirect from login screen once
        if (store.screen === 'login') {
            // Check if there's a pending invite to handle after login
            const params = new URLSearchParams(window.location.search);
            const inviteToken = params.get('invite');
            if (inviteToken) {
                store.acceptInvite(inviteToken)
                    .then(() => {
                        // Clear invite from URL
                        window.history.replaceState({}, '', window.location.pathname);
                        store.setCurrentScreen('organization');
                    })
                    .catch((err: unknown) => {
                        logger.error('Failed to accept invite', {
                            error: err instanceof Error ? err.message : String(err),
                        });
                        store.setCurrentScreen('camera');
                    });
            } else {
                store.setCurrentScreen('camera');
            }
        }

        const id = window.setTimeout(() => {
            useStore.getState().fetchGps();
        }, 1000);

        // Background preload of the on-device Whisper model.
        //
        // Why here: the user opens the camera screen on every login, but
        // typically waits a few seconds before pressing the capture button.
        // We use that idle window to download (~75MB) + decode the model
        // so the voice-note screen feels instant when reached. If the user
        // never hits voice-note, the wasted bandwidth is one-time and
        // browser-cached.
        //
        // Why fire-and-forget: we don't block the UI on it, we don't
        // surface progress globally. The voice-note screen still owns the
        // load progress UI as a fallback (in case the user gets there
        // before the preload completes, or the preload silently failed).
        //
        // Why requestIdleCallback: keeps the boot critical path clean —
        // login response + first paint + GPS prompt + projects fetch all
        // happen first. The ML lib is heavy (~3MB JS even before model
        // weights) and we don't want it competing for main-thread time.
        //
        // PWA persistence: transformers.js stores the model in the browser's
        // Cache Storage. That cache survives reloads, tab close, even
        // app installs to home screen. The model is fetched ONCE per
        // device — no service worker needed (and CLAUDE.md forbids one).
        const idleHandle: { id: number; isIdleCallback: boolean } = (() => {
            const w = window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
            if (typeof w.requestIdleCallback === 'function') {
                return { id: w.requestIdleCallback(() => { void preloadWhisperModel(); }, { timeout: 5000 }), isIdleCallback: true };
            }
            return { id: window.setTimeout(() => { void preloadWhisperModel(); }, 3000), isIdleCallback: false };
        })();

        return () => {
            window.clearTimeout(id);
            if (idleHandle.isIdleCallback) {
                const w = window as unknown as { cancelIdleCallback?: (id: number) => void };
                w.cancelIdleCallback?.(idleHandle.id);
            } else {
                window.clearTimeout(idleHandle.id);
            }
            // Abort the boot retry loop if the auth state flips before it
            // finishes (e.g. interceptor wipes the token mid-retry).
            bootCycleStopRef.current?.();
            bootCycleStopRef.current = null;
        };
    }, [isAuthenticated, performBootCycle]);

    useEffect(() => {
        if (debugInfo) logger.debug('Camera Debug Info', { info: debugInfo });
    }, [debugInfo]);

    const renderScreen = (): React.ReactElement => {
        if (!isAuthenticated) {
            return <LoginScreen />;
        }

        switch (screen) {
            case 'projects':
                return <SetProjectScreen />;
            case 'camera':
                return <CameraScreen />;
            case 'photo-upload':
                return <PhotoUploadScreen />;
            case 'positioning':
                return <PositioningScreen />;
            case 'result':
                return <ResultScreen />;
            case 'map-floor-plan-choice':
                return <MapFloorPlanChoiceScreen />;
            case 'map-choice':
                return <MapChoiceScreen />;
            case 'annotation':
                return <AnnotationScreen />;
            case 'voice-note':
                return <VoiceNoteScreen />;
            case 'organization':
                return <OrganizationScreen />;
            case 'paywall':
                return <PaywallScreen />;
            case 'onboarding':
                return <OnboardingScreen />;
            case 'billing':
                return <BillingScreen />;
            default:
                // Default to camera to ensure post-login lands on capture screen
                return <CameraScreen />;
        }
    };

    // Trial countdown banner: warn when 5 days or fewer remain.
    const daysLeft = subscription?.daysLeftInTrial;
    const showTrialBanner =
        isAuthenticated &&
        screen !== 'paywall' &&
        subscription?.status === 'trialing' &&
        typeof daysLeft === 'number' &&
        daysLeft >= 0 &&
        daysLeft <= 5;

    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onReset={handleErrorReset}
            onError={(error, errorInfo) => {
                logger.error('App ErrorBoundary caught error', {
                    error: error.message,
                    componentStack: errorInfo.componentStack
                });
            }}
        >
            <CheckoutReturnHandler />
            <div className="app-root w-screen flex flex-col font-sans bg-black overflow-hidden">
                <div className="app-root relative w-full flex flex-col bg-black overflow-hidden">
                    {/* Backdrop strategy:
                        - On unauthenticated screens (login, signup) and on the
                          auth-gated non-camera screens (onboarding, paywall)
                          we show a static AuthBackground. This avoids firing
                          the OS camera-permission prompt before the user has
                          any reason to grant it — permission now fires the
                          first time they hit the actual camera screen.
                        - On screens that actually need the live feed
                          (camera, positioning, annotation, projects,
                          manage-floor-plans) we mount BackgroundCamera.
                        They're mutually exclusive so mount/unmount cleanly. */}
                    {/* BackgroundCamera only when fully booted AND on a
                        camera-bearing screen — otherwise we'd trigger the
                        OS permission prompt during the boot loading/error
                        states, which the user has no context for. */}
                    {isAuthenticated && bootStatus === 'ready' && needsCamera(screen) ? (
                        <BackgroundCamera cameraReady={cameraReady} setCameraReady={handleSetCameraReady} setDebugInfo={handleSetDebugInfo} />
                    ) : (
                        <AuthBackground />
                    )}
                    <div className="absolute inset-0 flex flex-col bg-black bg-opacity-20">
                        {!isAuthenticated ? (
                            <>
                                <div className="flex-1 flex flex-col overflow-hidden flex-grow">
                                    <ScreenErrorBoundary screenName="Login">
                                        <Suspense fallback={<div className="flex-1 flex items-center justify-center text-yellow-400">Loading...</div>}>
                                            {renderScreen()}
                                        </Suspense>
                                    </ScreenErrorBoundary>
                                </div>
                                <div className="text-center py-1 border-t border-yellow-400/20 bg-black/30 flex justify-center items-center mt-auto" style={{ fontSize: 'min(2.1vw, 10px)' }}>
                                    <p className="text-base text-yellow-400/60 font-mono" style={{ fontSize: 'min(2.1vw, 10px)' }}>© {new Date().getFullYear()} PinCam</p>
                                </div>
                            </>
                        ) : bootStatus === 'loading' ? (
                            // Cold-start boot: spinner only, no app chrome.
                            // TopControls is intentionally hidden until
                            // subscription is known, so the user never sees
                            // the half-broken navbar that triggered the
                            // original report.
                            <div className="flex-1 flex flex-col overflow-hidden flex-grow">
                                <BootLoading />
                            </div>
                        ) : bootStatus === 'failed' ? (
                            <div className="flex-1 flex flex-col overflow-hidden flex-grow">
                                <BootError onRetry={handleBootRetry} onLogout={handleBootLogout} />
                            </div>
                        ) : (
                            <>
                                <TopControls
                                    handleBack={handleBack}
                                    logout={logout}
                                    isAuthenticated={isAuthenticated}
                                    // Collapse the navbar to just the logo
                                    // while the user hasn't completed the
                                    // subscription setup — nothing else in
                                    // the full navbar is meaningful yet
                                    // (no project to pick, settings moot).
                                    minimal={
                                        screen === 'onboarding' ||
                                        screen === 'paywall' ||
                                        !subscription?.onboardingCompleted
                                    }
                                />
                                {showTrialBanner && (
                                    <button
                                        type="button"
                                        onClick={() => useStore.getState().startCheckout()}
                                        className="w-full bg-yellow-400 text-black font-mono font-bold py-1 px-3 text-center text-xs hover:bg-yellow-300 flex-shrink-0 border-b-2 border-black"
                                        data-testid="trial-banner"
                                    >
                                        {daysLeft === 0
                                            ? 'Trial in scadenza oggi — attiva l\'abbonamento →'
                                            : `Trial: ${daysLeft} ${daysLeft === 1 ? 'giorno' : 'giorni'} rimanenti — attiva ora →`}
                                    </button>
                                )}
                                <div className="flex-1 flex flex-col min-h-0 flex-grow">
                                    <ScreenErrorBoundary screenName={screen}>
                                        <Suspense fallback={<div className="flex-1 flex items-center justify-center text-yellow-400">Loading...</div>}>
                                            <div className="flex-1 min-h-0 flex-grow">
                                                {renderScreen()}
                                            </div>
                                        </Suspense>
                                    </ScreenErrorBoundary>
                                </div>
                                {/* Global drawer — opened from any caller via
                                    openFloorPlanDrawer(projectId) on the store.
                                    Portaled to document.body, so position in
                                    the tree is just a place to put the mount. */}
                                <FloorPlanBottomSheet />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
}

export default App;
